import { useI18N } from '@susu/i18n/client'
import {
  Button,
  Checkbox,
  InputField,
  InputPhoneNumber,
} from '@susu/ui/components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'

import Avo, { ErrorType } from '../../../Avo/Avo'
import { ScreenLayout } from '../../components/ScreenLayout'
import { SlotUnavailable } from '../../components/SlotUnavailable'
import { datadogLog, flowProperties } from '../../lib/analytics'
import { useBookingStore } from '../../store'
import { createBooking, isSlotUnavailableError } from './api/user-data-api'
import type { UserDataErrors, UserInfo } from './UserData.types'
import {
  FIELD_MAX_LENGTH,
  normalizeUserInfo,
  sha256,
  validateUserInfo,
} from './UserData.utils'

interface UserDataProps {
  onClose?: () => void
}

const EMPTY_USER: UserInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  notes: '',
  isSubscribed: true,
}

/** Text fields that share the same change handler. */
type TextField = 'firstName' | 'lastName' | 'email' | 'notes'

/**
 * Final step of the booking flow: collects customer details and creates the
 * appointment. React rebuild of the legacy `user-data` Stencil component.
 */
export const UserData = ({ onClose }: UserDataProps) => {
  const { t } = useI18N()
  const { userData, location, employeeId, serviceId, setState, goBack } =
    useBookingStore()
  const queryClient = useQueryClient()
  // Host did not pass a service — show purpose flow after create (legacy
  // GrowthBook path replaced by this explicit consumer contract).
  const needsPreAppointmentPurpose = !serviceId

  const [user, setUser] = useState<UserInfo>(() => ({
    ...EMPTY_USER,
    ...userData,
  }))
  const [errors, setErrors] = useState<UserDataErrors>({})
  const [isPhoneValid, setIsPhoneValid] = useState(false)
  // Read while deciding the phone message: `onValidityChange` lands in the same
  // tick as `onFullNumberChange`, before a state update would be visible.
  const isPhoneValidRef = useRef(false)
  const [honeypot, setHoneypot] = useState('')

  // `InputPhoneNumber` wraps an uncontrolled input: feed it the restored number
  // once, then let `onFullNumberChange` drive our state. Passing the controlled
  // value back would re-format the field on every keystroke.
  const [initialPhone] = useState(() => userData?.phone ?? '')

  // Set synchronously on submit, before the `await sha256(...)` below —
  // `createMutation.isPending` only flips once `.mutate()` actually runs, so a
  // second click landing inside that await would sneak past it and double-book.
  const isSubmittingRef = useRef(false)
  // Read by `onSuccess` below — the digest is computed before `.mutate()` so
  // it is ready the moment the booking API returns.
  const ehashRef = useRef('')

  const messages = useMemo(
    () => ({
      required: t('userData.errors.required'),
      invalidEmail: t('userData.errors.invalidEmail'),
      requiredPhone: t('userData.errors.requiredPhone'),
      invalidPhone: t('userData.errors.invalidPhone'),
    }),
    [t],
  )

  const createMutation = useMutation({
    mutationFn: createBooking,
    onSuccess: (bookingId) => {
      // Fired here rather than before the request — this is "confirm
      // appointment", once per successful booking, with the id the API
      // actually created. `step_completed` moves with it so the two events
      // that mark this step done never disagree about whether it happened.
      Avo.bookingToolConfirmAppointment({
        ...flowProperties(),
        bookingId,
        eHash: ehashRef.current,
      })
      Avo.bookingToolStepCompleted(flowProperties())

      // Clearing history stops the user from stepping back into a completed
      // booking, the same rule the reschedule flow follows.
      setState({
        bookingId,
        screen: 'confirmation',
        screenHistory: [],
        confirmationVariant: needsPreAppointmentPurpose
          ? 'appointment-confirmed'
          : 'booked',
      })
    },
    onError: () => {
      isSubmittingRef.current = false
      Avo.bookingToolError({
        ...flowProperties(),
        errorType: ErrorType.BOOKING_FAILED,
      })
    },
  })

  const isSlotUnavailable = isSlotUnavailableError(createMutation.error)

  // Drop the taken slot and the availability it came from, so Timeslots
  // refetches instead of offering it again from the store / query cache.
  // `userData` stays in the store, so the form is prefilled on return.
  const handleChooseNewSlot = () => {
    setState({ timeslot: undefined, selectedEmployee: undefined })
    queryClient.removeQueries({ queryKey: ['employee-for-timeslots'] })
    createMutation.reset()
    goBack()
  }

  // Load analytics — mirrors the legacy screen's `componentDidLoad`.
  useEffect(() => {
    Avo.bookingToolStepViewed(flowProperties())
  }, [])

  const handleTextChange =
    (field: TextField) => (event: React.ChangeEvent<HTMLElement>) => {
      const { value } = event.target as HTMLInputElement | HTMLTextAreaElement
      setUser((previous) => ({ ...previous, [field]: value }))
      // Clear the message as soon as the user starts fixing the field; it comes
      // back on the next submit if it is still invalid.
      if (field !== 'notes') {
        setErrors((previous) =>
          previous[field] ? { ...previous, [field]: undefined } : previous,
        )
      }
    }

  const handlePhoneValidityChange = (isValid: boolean) => {
    isPhoneValidRef.current = isValid
    setIsPhoneValid(isValid)
  }

  // `InputPhoneNumber` reports validity immediately before the full number, in
  // the same tick, so the message is decided here where both are known. Mirrors
  // the emptiness check in `validateUserInfo` so inline and submit agree.
  const handlePhoneNumberChange = (fullNumber: string) => {
    setUser((previous) => ({ ...previous, phone: fullNumber }))
    setErrors((previous) => {
      const phone = !fullNumber.trim()
        ? messages.requiredPhone
        : isPhoneValidRef.current
          ? undefined
          : messages.invalidPhone
      return previous.phone === phone ? previous : { ...previous, phone }
    })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (createMutation.isPending || isSubmittingRef.current) return

    if (honeypot) {
      const { serviceId, storeId } = useBookingStore.getState()
      datadogLog('bookingToolBotDetected', { serviceId, storeId, employeeId })
      Avo.bookingToolError({
        ...flowProperties(),
        errorType: ErrorType.CAPTCHA_FAILED,
      })
      return
    }

    const normalized = normalizeUserInfo(user)
    setUser(normalized)
    // Persist before validating so a failed attempt survives back-navigation.
    setState({ userData: normalized })

    const nextErrors = validateUserInfo(normalized, isPhoneValid, messages)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    // Set before the async digest below, not after `.mutate()` — that gap is
    // exactly where a second click would otherwise sneak past `isPending`.
    isSubmittingRef.current = true

    // Lowercased before hashing so the same address always digests to the
    // same eHash regardless of how the customer capitalised it.
    ehashRef.current = await sha256(normalized.email.toLowerCase())

    createMutation.mutate(normalized)
  }

  if (isSlotUnavailable) {
    return (
      <SlotUnavailable
        onClose={onClose}
        onChooseNewSlot={handleChooseNewSlot}
      />
    )
  }

  return (
    <ScreenLayout
      title={t('userData.title')}
      titleTestId="user-data-heading"
      onClose={onClose}
    >
      <form
        className="flex-1 flex flex-col overflow-hidden"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="flex-1 overflow-y-auto flex flex-col gap-20 pt-12 pb-24">
          <div className="flex flex-col gap-20 xl:flex-row">
            <InputField
              id="user-data-first-name"
              name="given-name"
              autoComplete="given-name"
              label={t('userData.firstName')}
              value={user.firstName}
              onChange={handleTextChange('firstName')}
              error={errors.firstName}
              maxLength={FIELD_MAX_LENGTH.name}
              data-testid="user-data-first-name"
            />
            <InputField
              id="user-data-last-name"
              name="family-name"
              autoComplete="family-name"
              label={t('userData.lastName')}
              value={user.lastName}
              onChange={handleTextChange('lastName')}
              error={errors.lastName}
              maxLength={FIELD_MAX_LENGTH.name}
              data-testid="user-data-last-name"
            />
          </div>

          {/*
            `type="text"` with `inputMode="email"` keeps the email keyboard on
            mobile while leaving validation messages to us — `type="email"`
            would surface the browser's own, untranslated copy.
          */}
          <InputField
            id="user-data-email"
            name="email"
            type="text"
            inputMode="email"
            autoComplete="email"
            label={t('userData.email')}
            value={user.email}
            onChange={handleTextChange('email')}
            error={errors.email}
            maxLength={FIELD_MAX_LENGTH.email}
            data-testid="user-data-email"
          />

          <InputPhoneNumber
            countryCode={
              initialPhone.startsWith('+') ? undefined : location.toUpperCase()
            }
            value={initialPhone}
            onValidityChange={handlePhoneValidityChange}
            onFullNumberChange={handlePhoneNumberChange}
            error={errors.phone}
            data-testid="user-data-phone"
          />

          {/* Notes move to pre-appointment-purpose when no serviceId was passed. */}
          {!needsPreAppointmentPurpose && (
            <textarea
              id="user-data-notes"
              name="notes"
              className="w-full h-96 resize-none rounded-md border border-border bg-background px-[19px] py-[9px] text-base leading-base font-light text-text outline-none transition-[border-color] duration-150 placeholder:text-text-placeholder hover:border-border-pressed focus:border-border-selected"
              placeholder={t('userData.notesPlaceholder')}
              value={user.notes}
              onChange={handleTextChange('notes')}
              maxLength={FIELD_MAX_LENGTH.notes}
              data-testid="user-data-note"
            />
          )}

          <Checkbox
            name="isSubscribed"
            label={t('userData.newsletter')}
            checked={user.isSubscribed}
            onChange={(event) =>
              setUser((previous) => ({
                ...previous,
                isSubscribed: event.target.checked,
              }))
            }
            data-testid="user-data-newsletter-checkbox"
          />

          {/*
            Honeypot: hidden from humans and assistive tech, irresistible to
            bots that fill every field. A filled value silently drops the submit.
          */}
          <input
            type="text"
            name="username"
            value={honeypot}
            onChange={(event) => setHoneypot(event.target.value)}
            className="hidden"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>

        <div className="shrink-0 -mx-16 flex flex-col gap-12 border-t border-border px-20 pt-20 pb-[calc(20px+env(safe-area-inset-bottom,0px))]">
          {createMutation.isError && (
            <p
              className="text-[14px] leading-[20px] font-light text-text text-center"
              role="alert"
              data-testid="user-data-error"
            >
              {t('common.error')}
            </p>
          )}
          <Button
            type="submit"
            className="w-full"
            size="l"
            loading={createMutation.isPending}
            data-testid="user-data-submit-button"
          >
            {t('userData.submit')}
          </Button>
        </div>
      </form>
    </ScreenLayout>
  )
}
